import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import { orderBy } from 'lodash';

import styles from 'components/common/Tables/Cells/DynamicChipsCell/DynamicChipsCell.module.scss';
import { DynamicChipsTooltip } from 'components/common/Tables/Cells/DynamicChipsCell/components/DynamicChipsTooltip/DynamicChipsTooltip';

import { Chip, ChipSize, ChipType } from 'components/DesignSystem/Chip';

export interface ResponsiveChipListIntersectionProps {
    /**
     *  array of strings to display as chips
     */
    list: string[];
    /**
     * Gap between chips in pixels
     */
    gap?: number;
    /**
     * The type/variant of the chip
     */
    type?: ChipType;
    /**
     * The size of the chip
     */
    size?: ChipSize;
    /**
     * Custom icon to display (for project type)
     */
    icon?: ReactElement;
    /**
     * If true, sorts the chips alphabetically
     */
    sortAlphabetically?: boolean;
    /**
     * Additional class names for the wrapper div
     */
    className?: string;
    /**
     * If true, allows chips to wrap to multiple lines
     */
    wrap?: boolean;
}

/**
 * A responsive chip list using IntersectionObserver to detect overflow.
 * Renders all chips, observes which ones overflow, then hides them and shows "+ N".
 */
export const DynamicChipsCell = ({
    list,
    type = 'default',
    size = 'default',
    icon,
    gap = 8,
    sortAlphabetically = true,
    className = '',
    wrap = false,
}: ResponsiveChipListIntersectionProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
    const moreChipRef = useRef<HTMLDivElement>(null);

    const [visibleCount, setVisibleCount] = useState(list.length);
    const [isReady, setIsReady] = useState(false);

    const sortedItems = useMemo(
        () => (sortAlphabetically ? orderBy(list, [(item) => item.toLowerCase()], ['asc']) : list),
        [list, sortAlphabetically],
    );

    useEffect(() => {
        const container = containerRef.current;
        const moreChip = moreChipRef.current;

        if (!container || sortedItems.length === 0) {
            setVisibleCount(sortedItems.length);
            setIsReady(true);
            return;
        }

        const visibilityMap = new Map<number, boolean>();
        let moreChipVisible = false;

        const calculateVisibleCount = () => {
            let count = 0;
            for (let i = 0; i < sortedItems.length; i++) {
                if (visibilityMap.get(i)) {
                    count++;
                } else {
                    break;
                }
            }

            if (count < sortedItems.length && count > 0 && !moreChipVisible) {
                count = Math.max(1, count - 1);
            }

            setVisibleCount(count);
            setIsReady(true);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // Check if it's the overflow chip
                    if (entry.target === moreChip) {
                        moreChipVisible = entry.isIntersecting;
                    } else {
                        // Find chip index by ref
                        const index = chipRefs.current.indexOf(entry.target as HTMLDivElement);
                        if (index !== -1) {
                            visibilityMap.set(index, entry.isIntersecting);
                        }
                    }
                });
                calculateVisibleCount();
            },
            { root: container, threshold: 0.99 },
        );

        // Observe all chip refs
        chipRefs.current.forEach((chip) => chip && observer.observe(chip));
        if (moreChip) observer.observe(moreChip);

        // Re-observe on resize
        const resizeObserver = new ResizeObserver(() => {
            visibilityMap.clear();
            moreChipVisible = false;
            //Re-observing forces the observer to check the current intersection state and fire the callback with fresh isIntersecting values.
            chipRefs.current.forEach((chip) => {
                if (chip) {
                    observer.unobserve(chip);
                    observer.observe(chip);
                }
            });
            if (moreChip) {
                observer.unobserve(moreChip);
                observer.observe(moreChip);
            }
        });
        resizeObserver.observe(container);

        return () => {
            observer.disconnect();
            resizeObserver.disconnect();
        };
    }, [sortedItems]);

    const hiddenCount = sortedItems.length - visibleCount;
    const hiddenItems = sortedItems.slice(visibleCount);

    return (
        <div className={`${styles.wrapper} ${className}`}>
            {/* Hidden container for intersection detection */}
            <div
                ref={containerRef}
                className={styles.observerContainer}
                style={{ gap: `${gap}px`, flexWrap: wrap ? 'wrap' : 'nowrap' }}
                aria-hidden={'true'}
            >
                {sortedItems.map((label, index) => (
                    <div key={index} ref={(el) => (chipRefs.current[index] = el)} className={styles.chipWrapper}>
                        <Chip label={label} type={type} size={size} icon={icon} />
                    </div>
                ))}
                <div ref={moreChipRef} className={styles.chipWrapper}>
                    <Chip label={`+ ${sortedItems.length}`} type={type} size={size} />
                </div>
            </div>

            {/* Visible container */}
            {isReady && (
                <div className={styles.container} style={{ gap: `${gap}px`, flexWrap: wrap ? 'wrap' : 'nowrap' }}>
                    {sortedItems.slice(0, visibleCount).map((label, index) => (
                        <Chip key={index} label={label} type={type} size={size} icon={icon} />
                    ))}
                    {hiddenCount > 0 && (
                        <DynamicChipsTooltip
                            hiddenItems={hiddenItems}
                            type={type}
                            size={size}
                            hiddenCount={hiddenCount}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
